/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect } from 'react';
import { Divider, Row, Col, Tabs, Skeleton } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/react-hooks';
import formatCurrency from 'shared/utils/formatCurrency';
import { color } from 'shared/utils/styles';
import { Tag, Text, SelectFieldSearchCustomers } from 'shared/components';
import BUILD_COD_OUT_PARCEL_QUERY, {
  BuildCodOutParcelData,
  BuildCodOutParcelVars,
} from 'shared/graphql/query/buildCodOutParcel';
import {
  CodContext,
  SelectedCodItemType,
} from 'views/CashOnDelivery/Routes/CashOnDeliveryByParcel/common/context/CodContext';
import CodList from './CodList';

const { TabPane } = Tabs;

const CreatePaymentCod: React.FC = () => {
  const { selectedCodItems, setSelectedCodItems } = useContext(CodContext);

  const { data, loading, error } = useQuery<BuildCodOutParcelData, BuildCodOutParcelVars>(
    BUILD_COD_OUT_PARCEL_QUERY,
    {
      variables: {
        parcel_orders: selectedCodItems.codItemsId,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (data?.buildCodOutParcel) {
      const { total_cod, parcel_orders } = data.buildCodOutParcel;
      setSelectedCodItems(
        (prevState): SelectedCodItemType => ({
          ...prevState,
          confirmPaymentCod: {
            totalCod: total_cod,
            codItems: parcel_orders.map((item) => ({
              codNumber: item.cod_in_number,
              totalCodItem: item.cod_price,
              trackingNumber: item.tracking_number,
              codInDate: item.cod_in_date,
            })),
          },
        }),
      );
    }
  }, [data?.buildCodOutParcel, setSelectedCodItems]);

  let content = <Skeleton loading={loading} />;

  if (data?.buildCodOutParcel) {
    const { total_cod } = data.buildCodOutParcel;

    content = (
      <>
        <Text strong>เงื่อนไขการทำจ่าย</Text>
        <Divider style={{ margin: '10px 0' }} />
        <Row gutter={[32, 16]}>
          <Col span={8} style={{ borderRight: `1px dashed ${color.grey}` }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Row justify="space-between">
                  <Col>
                    ผู้รับเงิน <Text customType="grey">(เจ้าหนี้)</Text>
                    <Text customType="danger">*</Text>
                  </Col>
                  <Col span={24}>
                    <SelectFieldSearchCustomers
                      value={{
                        key: selectedCodItems.customerId,
                        label: selectedCodItems.customerFullName,
                      }}
                      disabled
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col span={8} style={{ borderRight: `1px dashed ${color.border}` }}>
            <Row>
              <Col span={10}>ที่อยู่:</Col>
              <Col span={14}>
                <Text strong>{selectedCodItems.customerAddress || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>เลขประจำตัวผู้เสียภาษี:</Col>
              <Col span={14}>
                <Text strong>{selectedCodItems.customerTaxId || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>เบอร์โทรศัพท์:</Col>
              <Col span={14}>
                <Text strong>
                  {selectedCodItems.customerPhone.length === 0
                    ? '-'
                    : selectedCodItems.customerPhone.join(', ')}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>อีเมล:</Col>
              <Col span={14}>
                <Text strong>
                  {selectedCodItems.customerEmail.length === 0
                    ? '-'
                    : selectedCodItems.customerEmail.join(', ')}
                </Text>
              </Col>
            </Row>
          </Col>

          <Col span={8}>
            <Row>
              <Col span={8}>รหัส COD-OUT:</Col>
              <Col span={16}>
                <Text strong>-</Text>
              </Col>
            </Row>
            <Row>
              <Col span={8}>มูลค่า CODรวม (บาท):</Col>
              <Col span={16}>
                <Text strong customType="primary" style={{ lineHeight: 1, fontSize: 24 }}>
                  {total_cod ? formatCurrency(total_cod) : '-'}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col span={8}>สถานะ:</Col>
              <Col span={16}>
                <Tag customColor="grey">กำลังสร้าง...</Tag>
              </Col>
            </Row>
          </Col>
        </Row>

        <Divider style={{ margin: 0, marginTop: -5 }} />

        <Tabs defaultActiveKey="1" animated={false}>
          <TabPane
            tab={
              <span>
                รายการ Tracking Number{' '}
                <CheckCircleOutlined
                  style={{
                    color: color.success,
                    marginLeft: 5,
                  }}
                />
              </span>
            }
            key="1"
          >
            <CodList />
          </TabPane>
        </Tabs>
      </>
    );
  }

  if (error) {
    content = <div>{error.message}</div>;
  }

  return content;
};

export default CreatePaymentCod;
